import { RxStatsDto, RxStatsResponseDto } from "../../../../api-client";
import { useEffect, useState, useMemo } from 'react';
import useStore from "../../../../helpers/useStore";
import { BIContext } from "../../../../stores/BIDashboard/biDashboard.provider";
import { calculateTotalRxCount } from "../../helpers/rxStatsHelpers";
import { useDebounce } from "../../../Transactions/Insurance/Base/hooks/useDebounce";

interface TotalRxCountData {
  locationId: string, 
  locationName: string,
  date: Date, 
  totalCount: number
}

const useAllRxCountStats = (binType: 'day' | 'week' | 'month' | 'year', startDate: Date, endDate: Date, locationIds?: string[], rxType?: string) => {
  const BIStore = useStore(BIContext); 
  const { getRxBasicStats } = BIStore; 

  // Debounce date changes
  const debounceStartDate = useDebounce(startDate, 300);
  const debounceEndDate = useDebounce(endDate, 300);

  // Memoize locationIds to avoid unnecessary re-renders
  const memoizedLocationIds = useMemo(() => {
    if (!locationIds || locationIds.length === 0) {
      return undefined;
    }
    return locationIds;
  }, [locationIds?.join(','), locationIds?.length]);

  // Store all fetched stats (for "all locations")
  const [allRxStats, setAllRxStats] = useState<RxStatsDto[]>([]);
  // Store filtered stats per selected locations
  const [rxStats, setRxStats] = useState<RxStatsDto[]>([]);

  const [totalRxCountResults, setTotalRxCountResults] = useState<TotalRxCountData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fallbackUsed, setFallbackUsed] = useState<boolean>(false);
  const [usedStartDate, setUsedStartDate] = useState<Date | undefined>(undefined);
  const [usedEndDate, setUsedEndDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        let response: RxStatsResponseDto | undefined;
        if (debounceStartDate && debounceEndDate) {
          // console.log(`[All Rx Count] Fetching data for date range: ${debounceStartDate} - ${debounceEndDate}`);
          // Always fetch all locations data, don't pass location filter to backend
          response = await getRxBasicStats(debounceStartDate, debounceEndDate, undefined, rxType);
          // console.log("[All Rx Count] Backend responded with:", response);
        } else {
          // console.log("[All Rx Count] Fetching all data");
        }
        
        if (response && response.rxStats.length > 0) {
          setAllRxStats(response.rxStats);
          
          // Apply location filter immediately when setting data
          if (memoizedLocationIds && memoizedLocationIds.length > 0) {
            setRxStats(response.rxStats.filter(p => memoizedLocationIds.includes(p.locationId)));
          } else {
            setRxStats(response.rxStats);
          }
          
          setFallbackUsed(response.fallbackUsed);
          setUsedStartDate(response.usedStartDate ? new Date(response.usedStartDate) : undefined);
          setUsedEndDate(response.usedEndDate ? new Date(response.usedEndDate) : undefined);
        } else {
          // console.log("[All Rx Count] No data found for the specified range, even with fallback.");
          setAllRxStats([]);
          setRxStats([]);
        }
      } catch (error) {
        // console.log("[All Rx Count] Error fetching Rx stats: ", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [debounceStartDate, debounceEndDate, getRxBasicStats, rxType]);

  // Filter locally whenever locations change (but not when data is initially loaded)
  useEffect(() => {
    if (!isLoading && allRxStats.length > 0) {
      if (memoizedLocationIds && memoizedLocationIds.length > 0) {
        setRxStats(allRxStats.filter(p => memoizedLocationIds.includes(p.locationId)));
      } else {
        // No location selected → show all
        setRxStats(allRxStats);
      }
    }
  }, [memoizedLocationIds, allRxStats, isLoading]);

  useEffect(() => {
    if (rxStats.length > 0) {
      const totalRxCounts = calculateTotalRxCount(rxStats, binType);
      setTotalRxCountResults(totalRxCounts);
    } else {
      setTotalRxCountResults([]);
    }
  }, [binType, rxStats]);

  const availableLocations = useMemo(() => {
    const locMap = new Map<string, string>();
    allRxStats.forEach((p) => {
      if (p.locationId) locMap.set(p.locationId, p.locationName || p.locationId);
    });
    return Array.from(locMap.entries()).map(([id, name]) => ({ id, name }));
  }, [allRxStats]);

  return { totalRxCountResults, isLoading, fallbackUsed, usedStartDate, usedEndDate, availableLocations };
};

export default useAllRxCountStats;
